"use client";

import { useEffect, useRef, useState } from "react";
import { useLanguage } from "../i18n/LanguageProvider";
import { readConsent, writeConsent } from "../../lib/cookie-consent";
import { setFabInset, clearFabInset } from "../../lib/talent/fab-inset";

type Copy = {
  heading: string;
  body: string;
  manage: string;
  acceptAll: string;
  rejectAll: string;
  save: string;
  essentialLabel: string;
  essentialDesc: string;
  analyticsLabel: string;
  analyticsDesc: string;
  advertisingLabel: string;
  advertisingDesc: string;
  required: string;
  privacyLink: string;
};

const copyByLocale: Record<string, Copy> = {
  ko: {
    heading: "쿠키 사용 안내",
    body: "Aply는 더 나은 서비스 제공을 위해 쿠키를 사용합니다. 필수 쿠키 외 분석·광고 쿠키는 동의 시에만 활성화됩니다.",
    manage: "설정",
    acceptAll: "전체 허용",
    rejectAll: "필수만 허용",
    save: "선택 저장",
    essentialLabel: "필수",
    essentialDesc: "로그인 세션, 보안 기능 등 서비스 이용에 반드시 필요한 쿠키",
    analyticsLabel: "분석",
    analyticsDesc: "Google Analytics 등을 통한 익명 사용 통계 수집",
    advertisingLabel: "광고",
    advertisingDesc: "Google AdSense를 통한 광고 노출 및 효과 측정",
    required: "항상 사용",
    privacyLink: "자세히 보기"
  },
  en: {
    heading: "We use cookies",
    body: "Aply uses cookies to deliver a better experience. Analytics and advertising cookies are activated only with your consent.",
    manage: "Manage",
    acceptAll: "Accept all",
    rejectAll: "Reject all",
    save: "Save selection",
    essentialLabel: "Essential",
    essentialDesc: "Required for login, security, and core site features.",
    analyticsLabel: "Analytics",
    analyticsDesc: "Anonymous usage stats via tools like Google Analytics.",
    advertisingLabel: "Advertising",
    advertisingDesc: "Google AdSense ad delivery and performance measurement.",
    required: "Always on",
    privacyLink: "Learn more"
  },
  "zh-CN": {
    heading: "我们使用 Cookie",
    body: "Aply 使用 Cookie 以提供更好的服务。除必要 Cookie 外，分析和广告 Cookie 仅在您同意后启用。",
    manage: "设置",
    acceptAll: "全部允许",
    rejectAll: "仅必要",
    save: "保存选择",
    essentialLabel: "必要",
    essentialDesc: "登录、安全及核心功能所必需。",
    analyticsLabel: "分析",
    analyticsDesc: "通过 Google Analytics 等收集匿名使用统计。",
    advertisingLabel: "广告",
    advertisingDesc: "通过 Google AdSense 投放广告并衡量效果。",
    required: "始终启用",
    privacyLink: "了解更多"
  },
  vi: {
    heading: "Chúng tôi sử dụng cookie",
    body: "Aply sử dụng cookie để mang lại trải nghiệm tốt hơn. Cookie phân tích và quảng cáo chỉ bật khi bạn đồng ý.",
    manage: "Cài đặt",
    acceptAll: "Chấp nhận tất cả",
    rejectAll: "Chỉ thiết yếu",
    save: "Lưu lựa chọn",
    essentialLabel: "Thiết yếu",
    essentialDesc: "Cần cho đăng nhập, bảo mật và các tính năng chính.",
    analyticsLabel: "Phân tích",
    analyticsDesc: "Thống kê sử dụng ẩn danh qua công cụ như Google Analytics.",
    advertisingLabel: "Quảng cáo",
    advertisingDesc: "Phân phối quảng cáo Google AdSense và đo lường hiệu quả.",
    required: "Luôn bật",
    privacyLink: "Tìm hiểu thêm"
  },
  ja: {
    heading: "Cookie を使用しています",
    body: "Aply はより良い体験を提供するために Cookie を使用します。分析・広告 Cookie は同意した場合のみ有効になります。",
    manage: "設定",
    acceptAll: "すべて許可",
    rejectAll: "必須のみ",
    save: "選択を保存",
    essentialLabel: "必須",
    essentialDesc: "ログイン、セキュリティ、主要機能に必要です。",
    analyticsLabel: "分析",
    analyticsDesc: "Google Analytics などによる匿名の利用統計。",
    advertisingLabel: "広告",
    advertisingDesc: "Google AdSense による広告配信と効果測定。",
    required: "常時オン",
    privacyLink: "詳細を見る"
  },
  id: {
    heading: "Kami menggunakan cookie",
    body: "Aply menggunakan cookie untuk pengalaman yang lebih baik. Cookie analitik dan iklan hanya aktif dengan persetujuan Anda.",
    manage: "Pengaturan",
    acceptAll: "Terima semua",
    rejectAll: "Hanya penting",
    save: "Simpan pilihan",
    essentialLabel: "Penting",
    essentialDesc: "Diperlukan untuk login, keamanan, dan fitur utama.",
    analyticsLabel: "Analitik",
    analyticsDesc: "Statistik penggunaan anonim via alat seperti Google Analytics.",
    advertisingLabel: "Iklan",
    advertisingDesc: "Penayangan iklan Google AdSense dan pengukuran kinerja.",
    required: "Selalu aktif",
    privacyLink: "Pelajari lebih lanjut"
  }
};

function pickCopy(locale: string): Copy {
  return copyByLocale[locale] ?? copyByLocale.en ?? copyByLocale.ko;
}

type ConsentDraft = { analytics: boolean; advertising: boolean };

export function CookieConsentBanner() {
  const { locale } = useLanguage();
  const copy = pickCopy(locale);

  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [draft, setDraft] = useState<ConsentDraft>({ analytics: true, advertising: true });
  const bannerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const stored = readConsent();
    if (!stored) {
      setVisible(true);
    }
  }, []);

  // 모바일에서만: 배너가 떠 있는 동안 그 높이만큼 우하단 피드백 FAB 을 밀어올려 '전체 허용'
  // 버튼을 가리지 않게 한다(데스크톱은 배너가 가운데 정렬이라 FAB 과 겹치지 않음).
  useEffect(() => {
    if (typeof window === "undefined" || !visible) return;
    const mq = window.matchMedia("(max-width: 767px)");
    const el = bannerRef.current;
    const apply = () => {
      if (mq.matches && el) setFabInset("cookie-banner", el.offsetHeight + 8);
      else clearFabInset("cookie-banner");
    };
    apply();
    mq.addEventListener("change", apply);
    const ro = el ? new ResizeObserver(apply) : null; // 상세 펼침 등으로 높이가 바뀔 때 반영.
    if (el && ro) ro.observe(el);
    return () => {
      mq.removeEventListener("change", apply);
      ro?.disconnect();
      clearFabInset("cookie-banner");
    };
  }, [visible, showDetails]);

  if (!visible) return null;

  const decide = (consent: ConsentDraft) => {
    writeConsent(consent);
    setVisible(false);
  };

  return (
    <div ref={bannerRef} className="fixed inset-x-0 bottom-0 z-[60] px-3 pb-3 sm:px-6 sm:pb-6">
      <div className="mx-auto max-w-3xl rounded-2xl border border-border bg-card p-4 shadow-elevated md:p-5">
        <header className="mb-2">
          <h2 className="text-sm font-semibold text-foreground">{copy.heading}</h2>
        </header>
        <p className="text-xs leading-relaxed text-muted-foreground">
          {copy.body}{" "}
          <a href="/legal/privacy" className="text-primary underline">
            {copy.privacyLink}
          </a>
        </p>

        {showDetails ? (
          <div className="mt-4 space-y-3 rounded-xl border border-border/60 bg-muted/30 p-3">
            <ConsentRow
              title={copy.essentialLabel}
              description={copy.essentialDesc}
              checked
              disabled
              hint={copy.required}
            />
            <ConsentRow
              title={copy.analyticsLabel}
              description={copy.analyticsDesc}
              checked={draft.analytics}
              onChange={(value) => setDraft((prev) => ({ ...prev, analytics: value }))}
            />
            <ConsentRow
              title={copy.advertisingLabel}
              description={copy.advertisingDesc}
              checked={draft.advertising}
              onChange={(value) => setDraft((prev) => ({ ...prev, advertising: value }))}
            />
          </div>
        ) : null}

        <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
          {!showDetails ? (
            <button
              type="button"
              onClick={() => setShowDetails(true)}
              className="text-xs text-muted-foreground underline-offset-4 hover:underline"
            >
              {copy.manage}
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => decide({ analytics: false, advertising: false })}
            className="inline-flex h-9 items-center justify-center rounded-md border border-input bg-background px-3 text-xs font-medium hover:bg-muted"
          >
            {copy.rejectAll}
          </button>
          {showDetails ? (
            <button
              type="button"
              onClick={() => decide(draft)}
              className="inline-flex h-9 items-center justify-center rounded-md bg-foreground/10 px-3 text-xs font-medium text-foreground hover:bg-foreground/15"
            >
              {copy.save}
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => decide({ analytics: true, advertising: true })}
            className="inline-flex h-9 items-center justify-center rounded-md bg-foreground px-3 text-xs font-semibold text-background hover:bg-foreground/90"
          >
            {copy.acceptAll}
          </button>
        </div>
      </div>
    </div>
  );
}

function ConsentRow({
  title,
  description,
  checked,
  onChange,
  disabled,
  hint
}: {
  title: string;
  description: string;
  checked: boolean;
  onChange?: (value: boolean) => void;
  disabled?: boolean;
  hint?: string;
}) {
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <input
        type="checkbox"
        className="mt-1 h-4 w-4 accent-foreground"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange?.(event.target.checked)}
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-foreground">{title}</span>
          {hint ? <span className="text-[11px] text-muted-foreground">({hint})</span> : null}
        </div>
        <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{description}</p>
      </div>
    </label>
  );
}
